import { checkRun, type PostedRun } from '../meta/online/run';
import { addToOutbox, sendOutcome } from '../meta/online/outbox';
import { RULES_VERSION } from '../meta/rulesVersion';
import { CROWN_COSMETIC } from '../content/cosmetics';
import { AWARD_COINS, settleAwards, type AwardKind, type AwardPayout } from '../meta/online/awards';
import {
  AddFriendAnswerSchema,
  AwardListSchema,
  BlockListSchema,
  BoardResponseSchema,
  ChallengeCreatedSchema,
  ChallengeListSchema,
  ChallengeSchema,
  CommunityListSchema,
  FeaturedSchema,
  FriendCardSchema,
  FriendCodeAnswerSchema,
  FriendCodeSchema,
  FriendListSchema,
  GhostResponseSchema,
  JoinResponseSchema,
  PlayerInfoSchema,
  PostRunResponseSchema,
  type BlockList,
  type BoardResponse,
  type Challenge,
  type ChallengeList,
  type CommunityList,
  type Featured,
  type FriendCard,
  type FriendList,
  type GhostResponse,
  type PlayerInfo,
  type PlayerName,
  type PostRunResponse,
} from '../meta/online/schema';
import { newestAnswerTo } from '../meta/online/news';
import type { Save } from '../meta/save/schema';
import { onAppShown } from '../platform/native';
import {
  apiRequest,
  APP_VERSION,
  onConnectionBack,
  ONLINE_ENABLED,
  probablyOnline,
} from '../platform/online';
import { saves, todayKey } from './records';
import { dailyScope } from '../meta/online/boards';
import { setNickname } from './nicknames';

/**
 * Online play in the game (docs/ONLINE.md, "The client"). Offline first: a finished run goes into
 * the save's outbox and is sent whenever there is a connection (now, at launch, when the app comes
 * back, when the connection returns). Nothing here ever holds the game up, and with online play
 * off nothing here does anything.
 */

/** Whether online play is on, the player has online scores on, and this version may post. */
export function onlineOn(): boolean {
  const save = saves().save;
  return (
    ONLINE_ENABLED && save.settings.onlineScores && save.online.updateNeededFor !== APP_VERSION
  );
}

/** Turns online scores on or off. Off also empties the outbox: nothing more is sent. */
export function setOnlineScores(on: boolean): void {
  saves().update((s) => {
    s.settings.onlineScores = on;
    if (!on) s.online.outbox = [];
  });
  if (on) void sendOutbox();
}

/** Starts sending at launch, and again whenever the app comes back or the connection returns. */
export function initOnline(): void {
  if (!ONLINE_ENABLED) return;
  // A game updated since the server asked for one may post again.
  const stale = saves().save.online.updateNeededFor;
  if (stale !== undefined && stale !== APP_VERSION) {
    saves().update((s) => delete s.online.updateNeededFor);
  }
  void sendOutbox();
  void checkChallengeNews();
  onAppShown(() => {
    void sendOutbox();
    void checkChallengeNews();
  });
  onConnectionBack(() => void sendOutbox());
}

/** The results of runs sent this session, newest last, for the screens that show ranks. */
const answered: { run: PostedRun; answer: PostRunResponse }[] = [];
export function sentRuns(): readonly { run: PostedRun; answer: PostRunResponse }[] {
  return answered;
}

type BoardResult = Extract<PostRunResponse, { status: 'ranked' }>['boards'][number];

/**
 * Where a run just queued placed on `board` (and `scope`, when given), once the outbox has sent
 * it: null when it ranked there on no board, could not be sent, or took longer than `waitMs`.
 * (The outbox keeps a copy of the run, so it is found by what it is, not by identity.)
 */
export async function boardResultFor(
  run: PostedRun,
  board: string,
  waitMs = 10_000,
): Promise<BoardResult | null> {
  if (!onlineOn()) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    sendOutbox(),
    new Promise<void>((done) => (timer = setTimeout(done, waitMs))),
  ]);
  clearTimeout(timer);
  const key = JSON.stringify(run);
  const sent = answered.find((a) => JSON.stringify(a.run) === key)?.answer;
  if (sent?.status !== 'ranked') return null;
  return sent.boards.find((b) => b.board === board) ?? null;
}

/** Puts a finished run in the outbox and tries to send it. */
export function queueRun(run: PostedRun): void {
  if (!onlineOn()) return;
  if (import.meta.env.DEV) {
    // The server will check it the same way: a mismatch here is a bug in the game's side.
    const check = checkRun(run);
    if (!check.ok) console.warn('[online] this run would be refused:', check.problem, run);
  }
  saves().update((s) => {
    s.online.outbox = addToOutbox(s.online.outbox, run, Date.now());
  });
  void sendOutbox();
}

let sending: Promise<void> | null = null;

/** Sends what the outbox holds, oldest first, stopping at the first run that must wait. */
export function sendOutbox(): Promise<void> {
  sending ??= drain().finally(() => (sending = null));
  return sending;
}

async function drain(): Promise<void> {
  while (onlineOn() && probablyOnline()) {
    const next = saves().save.online.outbox[0];
    if (!next) return;
    const token = await playerToken();
    if (!token) return;
    const answer = await apiRequest('/runs', { method: 'POST', body: next.run, token });
    const outcome = sendOutcome(answer.status);
    if (outcome === 'retry') return;
    if (outcome === 'update') {
      saves().update((s) => (s.online.updateNeededFor = APP_VERSION));
      return;
    }
    if (outcome === 'rejoin') {
      // The server no longer knows this player (deleted on another device): start a new one.
      saves().update((s) => delete s.online.player);
      continue;
    }
    if (outcome === 'done' && answer.status !== 'network') {
      const parsed = PostRunResponseSchema.safeParse(answer.body);
      if (parsed.success) answered.push({ run: next.run, answer: parsed.data });
    } else if (outcome === 'drop') {
      console.warn('[online] run refused:', answer.status !== 'network' ? answer.body : '');
    }
    // Sent or never sendable: either way it leaves the outbox (only this one, by identity, in
    // case a new run was added meanwhile).
    saves().update((s) => {
      s.online.outbox = s.online.outbox.filter(
        (p) => !(p.at === next.at && p.run.claimed.ticks === next.run.claimed.ticks),
      );
    });
  }
}

let joining: Promise<string | null> | null = null;

/** The player, joining first if the game has none yet. Null offline or when joining fails. */
export async function ensurePlayer(): Promise<OnlinePlayer | null> {
  if (!onlineOn()) return null;
  const token = await playerToken();
  return token ? (saves().save.online.player ?? null) : null;
}

export type OnlinePlayer = NonNullable<Save['online']['player']>;

/**
 * Renames the player (a name from the word lists). `ok` when the server took it; `offline` when it
 * could not be reached; `refused` when it is not a name the game makes.
 */
export async function renamePlayer(name: PlayerName): Promise<'ok' | 'offline' | 'refused'> {
  const player = await ensurePlayer();
  if (!player) return 'offline';
  const answer = await apiRequest('/me', { method: 'PATCH', body: { name }, token: player.token });
  if (answer.status === 200) {
    saves().update((s) => {
      if (s.online.player) s.online.player.name = name;
    });
    return 'ok';
  }
  return answer.status === 422 ? 'refused' : 'offline';
}

/** The player's token, joining first if the game has no player yet. Null when that fails. */
function playerToken(): Promise<string | null> {
  const player = saves().save.online.player;
  if (player) return Promise.resolve(player.token);
  joining ??= (async () => {
    const answer = await apiRequest('/players', { method: 'POST' });
    if (answer.status !== 201) return null;
    const parsed = JoinResponseSchema.safeParse(answer.body);
    if (!parsed.success) return null;
    saves().update((s) => (s.online.player = parsed.data));
    return parsed.data.token;
  })().finally(() => (joining = null));
  return joining;
}

const boards = new Map<string, BoardResponse>();

/**
 * A board, fresh when the server answers, else the last one seen this session (its `at` says how
 * old). Null when there is neither. `friends`: only the player and their friends (needs a player).
 */
export async function loadBoard(
  board: string,
  scope: string,
  { friends = false }: { friends?: boolean } = {},
): Promise<BoardResponse | null> {
  const key = `${board}/${scope}${friends ? '/friends' : ''}`;
  const token = saves().save.online.player?.token;
  if (friends && !token) return null;
  if (onlineOn() && probablyOnline()) {
    const query = friends ? '?friends=1' : '';
    const answer = await apiRequest(
      `/boards/${encodeURIComponent(board)}/${encodeURIComponent(scope)}${query}`,
      token ? { token } : {},
    );
    if (answer.status === 200) {
      const parsed = BoardResponseSchema.safeParse(answer.body);
      if (parsed.success) boards.set(key, parsed.data);
    }
  }
  return boards.get(key) ?? null;
}

/**
 * The best run on a board to race as a ghost: played on this game's rules (so it replays the same
 * here) and not flagged. Null when there is none, or offline.
 */
export async function loadGhost(board: string, scope: string): Promise<GhostResponse | null> {
  if (!onlineOn() || !probablyOnline()) return null;
  const answer = await apiRequest(
    `/ghosts/${encodeURIComponent(board)}/${encodeURIComponent(scope)}?rules=${RULES_VERSION}`,
  );
  if (answer.status !== 200) return null;
  const parsed = GhostResponseSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

let featured: Featured | null = null;
const featuredListeners = new Set<() => void>();

/** This week's featured lawn as last heard from the server (null before, or once its week ended). */
export function featuredNow(): Featured | null {
  return featured && featured.ends > Date.now() ? featured : null;
}

/**
 * Asks the server for this week's featured lawn (docs/ONLINE.md, "Boards"): kept for the session,
 * asked again once its week is over. Null offline.
 */
export async function loadFeatured(): Promise<Featured | null> {
  const known = featuredNow();
  if (known) return known;
  if (!onlineOn() || !probablyOnline()) return null;
  const answer = await apiRequest('/featured');
  if (answer.status !== 200) return null;
  const parsed = FeaturedSchema.safeParse(answer.body);
  if (!parsed.success) return null;
  featured = parsed.data;
  for (const fn of featuredListeners) fn();
  return featured;
}

/** Calls `fn` when the featured lawn arrives; returns the way to stop. */
export function onFeatured(fn: () => void): () => void {
  featuredListeners.add(fn);
  return () => featuredListeners.delete(fn);
}

// -- Friends (docs/ONLINE.md, "Boards") ------------------------------------------------------------

/**
 * Who to add (docs/FRIENDS.md, step 3): a secret friend code (shared by its owner, so friends at
 * once), or a player id (after a race, or a link from before friend codes: a request).
 */
export type FriendTarget = { code: string } | { player: string };

/** A friend code as shown, in two groups of four: `K7QX-2M9P`. */
export function friendCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/** What a friend link's `friend=` (or a pasted code) names: a friend code or a player id. */
export function friendTarget(value: string): FriendTarget | null {
  const code = value.toUpperCase().replace('-', '');
  if (FriendCodeSchema.safeParse(code).success) return { code };
  const id = value.toLowerCase().replace('-', '');
  return /^[0-9a-z]{10}$/.test(id) ? { player: id } : null;
}

/** The friend code or player id in pasted text (it may carry more words), or null. */
export function readFriendCode(text: string): FriendTarget | null {
  const link = /[?&]friend=([0-9a-zA-Z]{8,10})/.exec(text)?.[1];
  if (link) return friendTarget(link);
  const code = /\b([2-9A-HJKMNP-Za-hjkmnp-z]{4})-?([2-9A-HJKMNP-Za-hjkmnp-z]{4})\b/.exec(text);
  if (code) return { code: `${code[1]}${code[2]}`.toUpperCase() };
  const id = /\b([0-9a-zA-Z]{5})-?([0-9a-zA-Z]{5})\b/.exec(text);
  return id ? { player: `${id[1]}${id[2]}`.toLowerCase() } : null;
}

/** The link that makes a friend of this player: their friend code. */
export function friendLink(code: string): string {
  return `https://playlawnmower.com/?friend=${code}`;
}

/** The player's own secret friend code (kept in the save once known), or null offline. */
export async function myFriendCode(): Promise<string | null> {
  const known = saves().save.online.friendCode;
  if (known) return known;
  const player = await ensurePlayer();
  if (!player) return null;
  const answer = await apiRequest('/me/friend-code', { token: player.token });
  if (answer.status !== 200) return null;
  const parsed = FriendCodeAnswerSchema.safeParse(answer.body);
  if (!parsed.success) return null;
  saves().update((s) => (s.online.friendCode = parsed.data.code));
  return parsed.data.code;
}

/** Who a friend code or player id belongs to, or `missing`, or null offline. */
export async function lookupFriend(target: FriendTarget): Promise<PlayerInfo | 'missing' | null> {
  if (!('code' in target)) return playerInfo(target.player);
  if (!onlineOn() || !probablyOnline()) return null;
  const answer = await apiRequest(`/friend-codes/${encodeURIComponent(target.code)}`);
  if (answer.status === 404) return 'missing';
  if (answer.status !== 200) return null;
  const parsed = PlayerInfoSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/** Friends heard from the server this session (for "already friends"). */
const knownFriends = new Set<string>();

export function isFriend(id: string): boolean {
  return knownFriends.has(id);
}

/** Friends now (a request accepted in the INBOX), so no result offers ADD FRIEND for them. */
export function markFriend(id: string): void {
  knownFriends.add(id);
}

/** A player's name from their id, or `missing` (no such player), or null offline. */
export async function playerInfo(id: string): Promise<PlayerInfo | 'missing' | null> {
  if (!onlineOn() || !probablyOnline()) return null;
  const answer = await apiRequest(`/players/${encodeURIComponent(id)}`);
  if (answer.status === 404) return 'missing';
  if (answer.status !== 200) return null;
  const parsed = PlayerInfoSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/**
 * Adds a friend: by friend code, friends both ways at once; by player id, a request they accept
 * (`pending`), unless they asked first. The friend's name when sent; otherwise what went wrong.
 */
export async function addFriend(
  target: FriendTarget,
): Promise<{ info: PlayerInfo; pending: boolean } | string> {
  const player = await ensurePlayer();
  if (!player) return "Can't reach the server right now.";
  if ('player' in target && target.player === player.id) return "That's your own code.";
  if ('code' in target && target.code === saves().save.online.friendCode) {
    return "That's your own code.";
  }
  const answer = await apiRequest('/friends', {
    method: 'POST',
    body: target,
    token: player.token,
  });
  if (answer.status === 200) {
    const parsed = AddFriendAnswerSchema.safeParse(answer.body);
    if (parsed.success) {
      const { pending = false, ...info } = parsed.data;
      if (!pending) knownFriends.add(info.player);
      return { info, pending };
    }
  }
  if (answer.status === 409) return "That's your own code.";
  if (answer.status === 404) return 'No player has that code.';
  if (answer.status === 403) return "You can't add this player.";
  if (answer.status === 422) return 'That is too many friends.';
  return "Can't reach the server right now.";
}

/**
 * A friend's card (docs/FRIENDS.md, "The friend's card"): this player's numbers beside theirs,
 * today's Daily being the one this game plays. Null when it can't be had.
 */
export async function friendCard(id: string): Promise<FriendCard | null> {
  const player = saves().save.online.player;
  if (!player || !onlineOn() || !probablyOnline()) return null;
  const daily = encodeURIComponent(dailyScope(todayKey()));
  const answer = await apiRequest(`/friends/${encodeURIComponent(id)}?daily=${daily}`, {
    token: player.token,
  });
  if (answer.status !== 200) return null;
  const parsed = FriendCardSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/** No longer friends (for either player). True when done. */
export async function removeFriend(id: string): Promise<boolean> {
  const player = saves().save.online.player;
  if (!player) return false;
  const answer = await apiRequest(`/friends/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token: player.token,
  });
  if (answer.status === 204) {
    knownFriends.delete(id);
    setNickname(id, '');
  }
  return answer.status === 204;
}

/** The player's friends, or null offline (an empty list before the game has a player). */
export async function myFriends(): Promise<FriendList | null> {
  if (!onlineOn() || !probablyOnline()) return null;
  const player = saves().save.online.player;
  if (!player) return { friends: [] };
  const answer = await apiRequest('/friends', { token: player.token });
  if (answer.status !== 200) return null;
  const parsed = FriendListSchema.safeParse(answer.body);
  if (!parsed.success) return null;
  knownFriends.clear();
  for (const f of parsed.data.friends) knownFriends.add(f.player);
  return parsed.data;
}

// -- Holding bonuses -----------------------------------------------------------------------------

let awardsCheckedAt = -Infinity;

/**
 * Pays the holding bonuses the server says this player earned (src/meta/online/awards.ts) and not
 * yet paid: coins, and the crown's paint the first time. At most every 10 minutes, and only once
 * there is a player (it never joins). The payout, or null when there was nothing new.
 */
export async function claimAwards(): Promise<AwardPayout | null> {
  const player = saves().save.online.player;
  const now = Date.now();
  if (!onlineOn() || !probablyOnline() || !player || now - awardsCheckedAt < 10 * 60_000) {
    return null;
  }
  awardsCheckedAt = now;
  const answer = await apiRequest('/me/awards', { token: player.token });
  if (answer.status !== 200) return null;
  const parsed = AwardListSchema.safeParse(answer.body);
  if (!parsed.success) return null;
  const known = parsed.data.awards.flatMap((a) =>
    a.kind in AWARD_COINS ? [{ id: a.id, kind: a.kind as AwardKind }] : [],
  );
  const payout = settleAwards(saves().save.online.awardsPaid, known);
  if (payout.coins === 0 && !payout.crown) return null;
  saves().update((s) => {
    s.coins += payout.coins;
    s.online.awardsPaid = payout.paid;
    if (payout.crown && !s.cosmetics.owned.includes(CROWN_COSMETIC)) {
      s.cosmetics.owned.push(CROWN_COSMETIC);
    }
  });
  return payout;
}

/** Sends a cleared Daily as a challenge; the link to share, or null when that failed. */
export async function sendChallenge(run: PostedRun): Promise<string | null> {
  const player = await ensurePlayer();
  if (!player) return null;
  const answer = await apiRequest('/challenges', {
    method: 'POST',
    body: { run },
    token: player.token,
  });
  if (answer.status !== 201) return null;
  const parsed = ChallengeCreatedSchema.safeParse(answer.body);
  return parsed.success ? parsed.data.url : null;
}

/** A challenge from its link: the challenge, `closed`, `missing`, or null when offline. */
export async function loadChallenge(id: string): Promise<Challenge | 'closed' | 'missing' | null> {
  if (!onlineOn() || !probablyOnline()) return null;
  const answer = await apiRequest(`/challenges/${encodeURIComponent(id)}`);
  if (answer.status === 410) return 'closed';
  if (answer.status === 404) return 'missing';
  if (answer.status !== 200) return null;
  const parsed = ChallengeSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/** Posts a friend's go at a challenge (a cleared Daily on its lawn). Quiet when it fails. */
export async function answerChallenge(id: string, run: PostedRun): Promise<void> {
  const player = await ensurePlayer();
  if (!player) return;
  await apiRequest(`/challenges/${encodeURIComponent(id)}/answers`, {
    method: 'POST',
    body: { run },
    token: player.token,
  });
}

/** The player's challenges and the ones they answered, or null offline. */
export async function myChallenges(): Promise<ChallengeList | null> {
  const player = saves().save.online.player;
  if (!onlineOn() || !probablyOnline()) return null;
  // Not online yet (no player): nothing sent or answered, not a failure.
  if (!player) return { challenges: [] };
  const answer = await apiRequest('/challenges', { token: player.token });
  if (answer.status !== 200) return null;
  const parsed = ChallengeListSchema.safeParse(answer.body);
  if (!parsed.success) return null;
  const newest = newestAnswerTo(parsed.data, player.id);
  if (newest > newestAnswer) {
    newestAnswer = newest;
    if (challengeNews()) for (const fn of newsListeners) fn();
  }
  return parsed.data;
}

/** The newest friend's answer to the player's challenges, as last heard from the server. */
let newestAnswer = 0;
let newsCheckedAt = -Infinity;
const newsListeners = new Set<() => void>();

/**
 * Asks the server about the player's challenges (at launch and when the app comes back, at most
 * every 5 minutes), for the CLUB tab's dot. Only once there is a player: it never joins.
 */
export async function checkChallengeNews(): Promise<void> {
  const now = Date.now();
  if (now - newsCheckedAt < 5 * 60_000) return;
  newsCheckedAt = now;
  await myChallenges();
}

/** Whether a friend answered one of the player's challenges since they last looked. */
export function challengeNews(): boolean {
  return onlineOn() && newestAnswer > (saves().save.online.answersSeenAt ?? 0);
}

/** The player has seen their challenges: the dot goes. */
export function markChallengesSeen(): void {
  if (newestAnswer <= (saves().save.online.answersSeenAt ?? 0)) return;
  saves().update((s) => {
    s.online.answersSeenAt = newestAnswer;
  });
}

/** Calls `fn` when challenge news arrives; returns the way to stop. */
export function onChallengeNews(fn: () => void): () => void {
  newsListeners.add(fn);
  return () => newsListeners.delete(fn);
}

/** Shares a lawn with the community: its id, or what went wrong (a message to show). */
export async function shareLawnOnline(code: string): Promise<{ id: string } | string> {
  const player = await ensurePlayer();
  if (!player) return "Can't reach the server right now.";
  const answer = await apiRequest('/lawns', {
    method: 'POST',
    body: { code },
    token: player.token,
  });
  if (answer.status === 200 || answer.status === 201) {
    const id = (answer.body as { id?: unknown } | null)?.id;
    if (typeof id === 'string') return { id };
  }
  const error =
    (answer.status !== 'network' && (answer.body as { error?: unknown } | null)?.error) || null;
  return typeof error === 'string' ? error : "Can't reach the server right now.";
}

/** A page of community lawns (newest or most played; or the player's own), or null offline. */
export async function communityLawns(
  sort: 'new' | 'popular' | 'mine',
  cursor: string | null = null,
): Promise<CommunityList | null> {
  if (!onlineOn() || !probablyOnline()) return null;
  const token = saves().save.online.player?.token;
  if (sort === 'mine' && !token) return { lawns: [], next: null };
  const query = sort === 'mine' ? 'mine=1' : `sort=${sort}`;
  const answer = await apiRequest(
    `/lawns?${query}${cursor ? `&cursor=${cursor}` : ''}`,
    token ? { token } : {},
  );
  if (answer.status !== 200) return null;
  const parsed = CommunityListSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/** Counts a play of a community lawn (once per player; quiet when it fails). */
export async function countCommunityPlay(id: string): Promise<void> {
  const player = await ensurePlayer();
  if (!player) return;
  await apiRequest(`/lawns/${encodeURIComponent(id)}/plays`, {
    method: 'POST',
    token: player.token,
  });
}

/** Reports a community lawn. True when the server took it. */
export async function reportCommunityLawn(id: string): Promise<boolean> {
  const player = await ensurePlayer();
  if (!player) return false;
  const answer = await apiRequest(`/lawns/${encodeURIComponent(id)}/report`, {
    method: 'POST',
    token: player.token,
  });
  return answer.status === 204;
}

/** Takes the player's own community lawn down. True when it is gone. */
export async function takeDownCommunityLawn(id: string): Promise<boolean> {
  const player = saves().save.online.player;
  if (!player) return false;
  const answer = await apiRequest(`/lawns/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token: player.token,
  });
  return answer.status === 204 || answer.status === 404;
}

/** Blocks a player: their community lawns no longer show for this one. True when done. */
export async function blockPlayer(id: string): Promise<boolean> {
  const player = await ensurePlayer();
  if (!player) return false;
  const answer = await apiRequest('/blocks', {
    method: 'POST',
    body: { player: id },
    token: player.token,
  });
  return answer.status === 204;
}

/** Unblocks a player. True when done. */
export async function unblockPlayer(id: string): Promise<boolean> {
  const player = saves().save.online.player;
  if (!player) return false;
  const answer = await apiRequest(`/blocks/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token: player.token,
  });
  return answer.status === 204;
}

/** The players this player blocked, or null offline. */
export async function blockedPlayers(): Promise<BlockList | null> {
  const player = saves().save.online.player;
  if (!onlineOn() || !probablyOnline() || !player) return null;
  const answer = await apiRequest('/blocks', { token: player.token });
  if (answer.status !== 200) return null;
  const parsed = BlockListSchema.safeParse(answer.body);
  return parsed.success ? parsed.data : null;
}

/**
 * Deletes everything the server holds about this player, then forgets the player here. True when
 * the server confirmed it (or there was nothing to delete).
 */
export async function deleteOnlineData(): Promise<boolean> {
  const player = saves().save.online.player;
  if (!player) {
    setOnlineScores(false);
    return true;
  }
  const answer = await apiRequest('/me', { method: 'DELETE', token: player.token });
  if (answer.status !== 204 && answer.status !== 401) return false;
  // Online scores go off, so the game does not quietly join again with the next run.
  saves().update((s) => {
    delete s.online.player;
    delete s.online.friendCode;
    // The server forgot the push token with the player.
    delete s.online.pushSent;
    s.settings.friendAlerts = false;
    s.online.outbox = [];
    s.settings.onlineScores = false;
  });
  return true;
}
